import { useState, useEffect, useContext, useMemo } from "react";
import styles from "./styles.module.css";
import CloseIcon from "images/svg/Close.svg";
import { Checkbox, IconButton } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import ShopContext from "context/ShopContext";
import DataTable from "components/utils/DataTable";
import ButtonGray from "components/utils/CustomFields/ButtonGray";
import ButtonRed from "components/utils/CustomFields/ButtonRed";
import { amountRoundOff } from "helpers/NumberFormatHelper";
import QtyTextField from "components/utils/QtyTextField/QtyTextField";

export default function SplitOrderDialog({ open, onClose, sale, onSplit }) {
  const [splitQtyMap, setSplitQtyMap] = useState({});
  const { selectedShop } = useContext(ShopContext);

  const items = useMemo(() => sale?.items || [], [sale]);

  const getItemKey = (item, index) => {
    return item.localId || item.id || `item_${index}`;
  };

  useEffect(() => {
    if (open) {
      const initialMap = {};
      items.forEach((item, index) => {
        initialMap[getItemKey(item, index)] = 0;
      });
      setSplitQtyMap(initialMap);
    }
  }, [open, sale, items]);

  if (!open || !sale) return null;

  const handleQtyChange = (key, qty, val) => {
    const num = Math.min(Math.max(0, Number(val) || 0), qty);
    setSplitQtyMap((prev) => ({
      ...prev,
      [key]: num,
    }));
  };

  const handleToggleSelect = (key, qty) => {
    setSplitQtyMap((prev) => {
      const current = prev[key] || 0;
      return {
        ...prev,
        [key]: current === qty ? 0 : qty,
      };
    });
  };

  const handleSelectAll = () => {
    const isAllSelected = items.every((item, idx) => (splitQtyMap[getItemKey(item, idx)] || 0) === item.qty);
    const updated = {};
    items.forEach((item, idx) => {
      updated[getItemKey(item, idx)] = isAllSelected ? 0 : item.qty;
    });
    setSplitQtyMap(updated);
  };

  let totalQty = 0;
  let totalSplitQty = 0;
  let totalSplitAmount = 0;

  items.forEach((item, idx) => {
    const key = getItemKey(item, idx);
    const qty = Number(item.qty || 1);
    const sQty = splitQtyMap[key] || 0;
    const unitPrice = item.payable / qty;

    totalQty += qty;
    totalSplitQty += sQty;
    totalSplitAmount += unitPrice * sQty;
  });

  const isSplitDisabled = totalSplitQty === 0 || totalSplitQty === totalQty;

  const handleSplit = () => {
    const itemsToSplit = [];
    items.forEach((item, idx) => {
      const key = getItemKey(item, idx);
      const sQty = splitQtyMap[key] || 0;
      if (sQty > 0) {
        itemsToSplit.push({
          ...item,
          quantity: sQty,
          splitQty: sQty,
        });
      }
    });
    onSplit(itemsToSplit);
  };

  const allSelected =
    items.length > 0 && items.every((item, idx) => (splitQtyMap[getItemKey(item, idx)] || 0) === item.qty);
  const someSelected = items.some((item, idx) => (splitQtyMap[getItemKey(item, idx)] || 0) > 0) && !allSelected;

  const columns = [
    { id: "no", label: "No", type: "text", align: "left", width: "40px" },
    { id: "name", label: "Item", type: "text", align: "left" },
    { id: "qty", label: "Qty", type: "text", align: "center", width: "70px" },
    {
      id: "splitQtyControls",
      label: "Split Qty",
      type: "callback",
      align: "center",
      width: "140px",
      viewRender: (row) => {
        const key = row.itemKey;
        const currentQty = splitQtyMap[key] || 0;
        const maxQty = row.qty;

        return (
          <div className={styles.qtyControlGroup}>
            <IconButton
              size="small"
              onClick={() => handleQtyChange(key, maxQty, currentQty - 1)}
              disabled={currentQty <= 0}
              className={styles.qtyBtn}
            >
              <RemoveIcon fontSize="small" />
            </IconButton>
            <QtyTextField
              value={currentQty}
              onCommit={(val) => handleQtyChange(key, maxQty, val)}
              onChange={(val) => handleQtyChange(key, maxQty, val)}
              sx={{
                width: "45px",
                textAlign: "center",
                input: { textAlign: "center", fontWeight: 600, fontSize: "14px", padding: "2px" },
              }}
            />
            <IconButton
              size="small"
              onClick={() => handleQtyChange(key, maxQty, currentQty + 1)}
              disabled={currentQty >= maxQty}
              className={styles.qtyBtn}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </div>
        );
      },
    },
    {
      id: "splitAmount",
      label: "Split Amount",
      type: "callback",
      align: "right",
      width: "110px",
      viewRender: (row) => {
        const currentQty = splitQtyMap[row.itemKey] || 0;
        const unitPrice = row.payable / (row.qty || 1);
        const splitPayable = unitPrice * currentQty;
        return `${selectedShop?.currency_symbol || ""} ${amountRoundOff(splitPayable)}`;
      },
    },
    {
      id: "action",
      label: (
        <Checkbox
          checked={allSelected}
          indeterminate={someSelected}
          onChange={handleSelectAll}
          color="primary"
          sx={{ padding: 0 }}
        />
      ),
      type: "callback",
      align: "center",
      width: "60px",
      viewRender: (row) => {
        const currentQty = splitQtyMap[row.itemKey] || 0;
        return (
          <Checkbox
            checked={currentQty === row.qty}
            indeterminate={currentQty > 0 && currentQty < row.qty}
            onChange={() => handleToggleSelect(row.itemKey, row.qty)}
            color="primary"
            sx={{ padding: 0 }}
          />
        );
      },
    },
  ];

  const rows = items.map((item, index) => {
    const optionName = item.option?.label ? ` (${item.option.label})` : "";
    return {
      ...item,
      itemKey: getItemKey(item, index),
      no: String(index + 1).padStart(2, "0"),
      name: `${item.name || item.item_name || item.item?.name || "Item"}${optionName}`,
      origQty: item.qty,
    };
  });

  return (
    <div className={styles.popUp}>
      <div className={styles.contentWrapper}>
        <div className={styles.headInfoSec}>
          <h2 className={styles.dialogTitle}>
            Split Order / Cart:{" "}
            <span className={styles.tableHighlight}>
              {sale?.table?.area?.name ? `${sale.table.area.name} | ` : ""}
              {sale?.table?.name
                ? `${sale.table.name}${sale.table.chair?.name ? ` (${sale.table.chair.name})` : ""}`
                : ` #${sale?.local_id || ""}`}
            </span>
          </h2>
          <img src={CloseIcon} alt="Close" onClick={onClose} className={styles.closeIcon} />
        </div>

        <div className={styles.tableWrapper}>
          <DataTable columns={columns} rows={rows} rowKey="itemKey" />
        </div>

        <div className={styles.summarySection}>
          <div className={styles.summaryItem}>
            <span>Total Items to Split:</span>
            <strong>{totalSplitQty}</strong>
          </div>
          <div className={styles.summaryItem}>
            <span>Split Cart Total:</span>
            <strong className={styles.amountHighlight}>
              {selectedShop?.currency_symbol || ""} {amountRoundOff(totalSplitAmount)}
            </strong>
          </div>
        </div>

        <div className={styles.buttonSection}>
          <ButtonGray onClick={onClose} className={styles.btnCancel}>
            Cancel
          </ButtonGray>
          <ButtonRed onClick={handleSplit} disabled={isSplitDisabled} className={styles.btnSplit}>
            Split
          </ButtonRed>
        </div>
      </div>
    </div>
  );
}
